import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import GenericPromises from '../../../../../api/GenericPromises';
import { GridColDef, GridRowsProp, GridTreeNodeWithRender, GridValueGetterParams } from '@mui/x-data-grid';
import { Menuitem } from '../../../../../interfaces/Security/menu';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { CompanyBankAccount } from '../../../../../interfaces/Security/companies';
import { Spinner } from '../../../../../components/Commons/Spinner/Spinner';
import { Header } from '../../../../../components/Header';
import DataTable from '../../../../../components/Tables/GridTableMaterialUI/DataTable';
import { PrimaryButton } from '../../../../../theme/buttons';
import { AddCompanyBankAccounts } from './add';
import { AuthContext } from '../../../../../context/AuthContext';

import { DialogEntity } from '../../../../../components/Dialogs/DialogEntity';
import { UpdateCompanyBankAccounts } from './update';
import useSnackBar from '../../../../../components/Commons/SnackBar/useSnackBar';
import { useDates } from '../../../../../hooks/useDates';

export const TableCompanyBankAccounts = ({ ...props }) => {
  const [t] = useTranslation("global");
  const { GenericGetResource } = GenericPromises();
  const { GetResourceByUrl } = usePermissions();
  const { user } = useContext(AuthContext);
  const { TimeConverter } = useDates();
  const [numTemp, setNumTemp] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [unauthorized, setUnauthorized] = useState(true);
  const [openDialogAdd, setOpenDialogAdd] = useState(false);
  const [openDialogUpdate, setOpenDialogUpdate] = useState(false);
  const [companyBankAccountsData, setCompanyBankAccountsData] = useState<GridRowsProp>([]);
  const [resourceScreen, setResourceScreen] = useState<Menuitem>();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const [messageSnack, setMessageSnack] = useState("");
  const [myPreferences, setMyPreferences] = useState({});
  const [companyBankAccountPayload, setCompanyBankAccountPayload] = useState({});
  const [columns, setColumns] = useState<GridColDef<CompanyBankAccount>[]>([
    {
      field: 'account_alias', headerName: t("bankaccounts.fields.account_alias"), headerClassName: 'header-grid-table', flex: 1,
    },
    {
      field: 'bank_name', headerName: t("bankaccounts.fields.bank_name"), headerClassName: 'header-grid-table', flex: 1,
    },
    {
      field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
    { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
  ]);

  const loadPreferences = async () => {
    let myPreferences = await localStorage.getItem("grid-companybankaccounts");
    if (myPreferences != null) {
      setMyPreferences(JSON.parse(myPreferences));
    }
  }

  const loadColumnsOrder = async () => {
    let myOrderColumns = await localStorage.getItem("grid-companybankaccounts-columns");
    if (myOrderColumns != null) {
      let myJson = JSON.parse(myOrderColumns);
      for (let index = 0; index < myJson.length; index++) {
        const element = myJson[index];
        if (element['type'] == 'date') {
          element.headerName = t(`generic.${element.field}`);
          element.valueGetter = function (params: GridValueGetterParams<CompanyBankAccount, any, GridTreeNodeWithRender>) {
            return TimeConverter(params.value);
          };
        }
        else {
          if (element.field === "main_location") {
            element.valueGetter = function (params: GridValueGetterParams<CompanyBankAccount, any, GridTreeNodeWithRender>) {
              return params.value ? t("generic.yes") : t("generic.no");
            };
          }
          else if (element.field !== "last_update_user") {
            element.headerName = t(`bankaccounts.fields.${element.field}`);
          }
        }
      }
      setColumns(myJson);
    }
  }

  const handleAddRow = (newRow: CompanyBankAccount) => {
    let myRow = {
      ...newRow,
      creation_date: new Date(),
      last_update_user: user?.user_email,
      last_update_date: new Date()
    }
    setCompanyBankAccountsData((prevState) => [...companyBankAccountsData, myRow]);
    if (props.add) {
      setNumTemp(numTemp + 1);
    }
  };

  const handleUpdateRow = (updateRow: CompanyBankAccount) => {
    if (companyBankAccountsData.length === 0) {
      return;
    }
    setCompanyBankAccountsData((prevRows) => {
      const rowToUpdateIndex = companyBankAccountsData.findIndex(e => e.company_bank_account_id === updateRow.company_bank_account_id);

      return prevRows.map((row, index) =>
        index === rowToUpdateIndex ? updateRow : row,
      );
    });
  };

  const handleDeleteRow = (deleteRow: number) => {
    if (companyBankAccountsData.length === 0) {
      return;
    }
    setCompanyBankAccountsData((prevRows) => {
      const rowToDeleteIndex = companyBankAccountsData.findIndex(e => e.company_bank_account_id === deleteRow);
      return [
        ...companyBankAccountsData.slice(0, rowToDeleteIndex),
        ...companyBankAccountsData.slice(rowToDeleteIndex + 1),
      ];
    });
  };

  useEffect(() => {
    GenericGetResource("/companybankaccounts")
      .then(
        (response) => {
          setCompanyBankAccountsData(response.data.data);

          GetResourceByUrl("/companybankaccounts")
            .then((response) => {
              setResourceScreen((prev) => response);
              loadColumnsOrder();
              loadPreferences();
              setDataLoaded(true);
            })
            .catch((error) => {
              showSnackBar(error.message, "error");
              setMessageSnack(error.message);
              setUnauthorized(false);
            });
        }
      ).catch((error) => {
        showSnackBar(error.message, "error");
        setMessageSnack(error.message);
        setUnauthorized(false);
      });
  }, []);

  return (
    <div className='d-flex w-100 justify-content-center'>
      {!unauthorized && <div className='screen-container d-flex flex-column justify-content-center align-items-center'> <img alt='ERROR' style={{ height: "20rem", width: "20rem" }} src={require("../../../../../assets/img/error.webp")} /> <h2>{messageSnack}</h2> </div>}
      {unauthorized && !dataLoaded && <Spinner />}
      {dataLoaded && resourceScreen?.read && <div className='screen-container w-75'>
        {resourceScreen?.create === true &&
          <div className='d-flex flex-row-reverse my-1'>
            <PrimaryButton variant='outlined' className="my-1" onClick={() => { setOpenDialogAdd(true) }}>{t("generic.buttons.add")}</PrimaryButton>
          </div>
        }
        <div className="d-flex justify-content-center">
          <DataTable
            columns={columns}
            setColumns={setColumns}
            data={companyBankAccountsData}
            entityId={"company_bank_account_id"}
            entity={`CompanyBankAccount`}
            preferences={myPreferences}
            namePreferences={"grid-companybankaccounts"}
            nameOrderColumns={"grid-companybankaccounts-columns"}
            isChildren={true}
            setOpenDialog={setOpenDialogUpdate}
            setDataRow={setCompanyBankAccountPayload}
          />
        </div>
        <DialogEntity
          content={<AddCompanyBankAccounts numTemp={numTemp} add={props.add} openDialog={openDialogAdd} setOpenDialog={setOpenDialogAdd} company_id={props.company_id} AddNewRow={handleAddRow} UpdateRow={handleUpdateRow} />}
          open={openDialogAdd}
          setOpen={setOpenDialogAdd}
          title={<Header title={t("companybankaccounts.title-view")} size='md' />}
        />
        <DialogEntity
          content={<UpdateCompanyBankAccounts numTemp={numTemp} add={props.add} companyBankAccountPayload={companyBankAccountPayload} company_id={props.company_id} setOpenDialog={setOpenDialogUpdate} permissions={resourceScreen} DeleteRow={handleDeleteRow} UpdateRow={handleUpdateRow} />}
          open={openDialogUpdate}
          setOpen={setOpenDialogUpdate}
          title={<Header title={t("companybankaccounts.title-view")} size='md' />}
        />
        <SnackbarComponent />
      </div>
      }
    </div>
  )
}
